import React, { useState, useRef, useEffect, useCallback } from 'react';
import './M3NavigationDrawer.css';

/**
 * M3 Navigation Drawer — persistent or dismissible.
 *
 * @param {boolean} [open]
 * @param {Function} [onClose]
 * @param {'persistent'|'dismissible'|'temporary'} [variant='persistent']
 * @param {ReactNode} header — top section (logo, user info)
 * @param {ReactNode} content — navigation items
 * @param {ReactNode} [footer] — bottom section
 */
export default function M3NavigationDrawer({
  open = false,
  onClose,
  variant = 'persistent',
  header,
  content,
  footer
}) {
  const overlayRef = useRef(null);

  // Close on overlay click
  const handleOverlayClick = useCallback(() => {
    if (variant !== 'persistent' && onClose) onClose();
  }, [variant, onClose]);

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (e.key === 'Escape' && variant !== 'persistent') onClose?.();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open, variant, onClose]);

  if (variant === 'persistent') {
    return (
      <nav className="m3-navigation-drawer m3-navigation-drawer-persistent">
        <div className="m3-navigation-drawer-header">{header}</div>
        <div className="m3-navigation-drawer-content">{content}</div>
        {footer && <div className="m3-navigation-drawer-footer">{footer}</div>}
      </nav>
    );
  }

  return (
    <>
      {open && (
        <div
          ref={overlayRef}
          className="m3-navigation-drawer-overlay"
          onClick={handleOverlayClick}
          aria-hidden="true"
        />
      )}
      <aside
        className={`m3-navigation-drawer m3-navigation-drawer-${variant} ${
          open ? 'm3-navigation-drawer-open' : ''
        }`}
        role="navigation"
        aria-label="Main navigation"
      >
        <div className="m3-navigation-drawer-header">{header}</div>
        <div className="m3-navigation-drawer-content">{content}</div>
        {footer && <div className="m3-navigation-drawer-footer">{footer}</div>}
      </aside>
    </>
  );
}

/**
 * Navigation drawer item.
 */
export function NavDrawerItem({ icon, label, active, onClick, className = '' }) {
  return (
    <button
      className={`m3-nav-drawer-item ${active ? 'm3-nav-drawer-item-active' : ''} ${className}`}
      onClick={onClick}
      role="link"
    >
      <span className="material-symbols-rounded m3-nav-drawer-item-icon">{icon}</span>
      <span className="m3-nav-drawer-item-label">{label}</span>
    </button>
  );
}
